<!-- Debug Panel Slide-in -->
<div
  *ngIf="isPanelOpen"
  class="fixed inset-0 z-50 overflow-hidden"
>
  <!-- Overlay: a real button so closing works via keyboard too. The panel is
       a sibling, so its clicks never reach the overlay. -->
  <button
    type="button"
    class="absolute inset-0 h-full w-full cursor-default bg-black bg-opacity-50 transition-opacity"
    (click)="closePanel()"
    aria-label="Close debug panel"
  ></button>

  <!-- Panel -->
  <div
    class="fixed inset-y-0 right-0 flex max-w-full"
  >
    <div class="w-screen max-w-md">
      <div class="flex h-full flex-col bg-white shadow-xl">
        <!-- Header -->
        <div class="bg-black px-4 py-6 sm:px-6">
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-medium text-white">Debug Panel</h2>
            <button
              type="button"
              class="rounded-md text-red-600 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-white"
              (click)="closePanel()"
            >
              <span class="sr-only">Close panel</span>
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Tabs -->
          <div class="mt-4 flex space-x-2">
            <button
              (click)="setActiveTab('storage')"
              class="px-3 py-2 text-sm font-medium rounded-md transition-colors"
              [ngClass]="{
                'bg-white text-black': activeTab === 'storage',
                'bg-gray-800 text-gray-300 hover:bg-gray-700': activeTab !== 'storage'
              }"
            >
              Storage
            </button>
            <button
              (click)="setActiveTab('analytics')"
              class="px-3 py-2 text-sm font-medium rounded-md transition-colors"
              [ngClass]="{
                'bg-white text-black': activeTab === 'analytics',
                'bg-gray-800 text-gray-300 hover:bg-gray-700': activeTab !== 'analytics'
              }"
            >
              Analytics
            </button>
            <button
              (click)="setActiveTab('sync')"
              class="px-3 py-2 text-sm font-medium rounded-md transition-colors"
              [ngClass]="{
                'bg-white text-black': activeTab === 'sync',
                'bg-gray-800 text-gray-300 hover:bg-gray-700': activeTab !== 'sync'
              }"
            >
              Sync
              <span
                *ngIf="syncAuditCounts.failures > 0"
                class="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-red-600 text-white"
              >{{ syncAuditCounts.failures }}</span>
            </button>
            <button
              (click)="setActiveTab('environment')"
              class="px-3 py-2 text-sm font-medium rounded-md transition-colors"
              [ngClass]="{
                'bg-white text-black': activeTab === 'environment',
                'bg-gray-800 text-gray-300 hover:bg-gray-700': activeTab !== 'environment'
              }"
            >
              Environment
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="relative flex-1 overflow-y-auto px-4 py-6 sm:px-6">

          <!-- Local Storage Tab -->
          <div *ngIf="activeTab === 'storage'">
            <div class="space-y-4">
              <button
                type="button"
                class="w-full bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-colors"
                (click)="openLocalStorageModal()"
              >
                Open Storage Manager
              </button>
              <p class="text-sm text-gray-600">
                View and manage local storage data used by the application.
              </p>

              <!-- Reset User Section -->
              <div class="mt-6 pt-6 border-t border-gray-200">
                <h3 class="text-sm font-semibold text-gray-900 mb-2">Danger Zone</h3>
                <button
                  type="button"
                  class="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors font-medium"
                  (click)="resetUser()"
                >
                  Reset User Entirely
                </button>
                <p class="text-xs text-gray-500 mt-2">
                  Clears all data (localStorage + Firestore), creates NEW anonymous user with different UID, and redirects to login.
                </p>
              </div>
            </div>
          </div>

          <!-- Analytics Tab -->
          <div *ngIf="activeTab === 'analytics'">
            <div class="space-y-4">
              <!-- Controls -->
              <div class="flex items-center justify-between p-3 bg-gray-100 rounded-md">
                <span class="text-sm font-medium text-gray-700">Debug Mode</span>
                <button
                  (click)="toggleAnalyticsDebug()"
                  class="px-3 py-1 text-sm rounded-md transition-colors"
                  [ngClass]="{
                    'bg-black text-white': analyticsDebugMode,
                    'bg-gray-300 text-gray-700': !analyticsDebugMode
                  }"
                >
                  {{ analyticsDebugMode ? 'ON' : 'OFF' }}
                </button>
              </div>

              <div class="flex space-x-2">
                <button
                  type="button"
                  class="flex-1 bg-black text-white px-3 py-2 text-sm rounded-md hover:bg-gray-800"
                  (click)="refreshAnalyticsData()"
                >
                  Refresh
                </button>
                <button
                  type="button"
                  class="flex-1 bg-red-600 text-white px-3 py-2 text-sm rounded-md hover:bg-red-700"
                  (click)="clearAnalyticsHistory()"
                >
                  Clear
                </button>
              </div>

              <!-- Events List -->
              <div class="space-y-3">
                <h3 class="text-sm font-semibold text-gray-900">
                  Recent Events ({{ analyticsEvents.length }})
                </h3>

                <div *ngIf="analyticsEvents.length === 0" class="text-center py-8 text-gray-500 text-sm">
                  No analytics events recorded yet
                </div>

                <div *ngFor="let event of analyticsEvents" class="border border-gray-200 rounded-md p-3 bg-gray-50">
                  <div class="flex items-start justify-between mb-2">
                    <div class="font-mono text-sm font-semibold text-gray-900">
                      {{ event.name }}
                    </div>
                    <div class="text-xs text-gray-500">
                      {{ formatTimestamp(event.timestamp) }}
                    </div>
                  </div>
                  <details class="text-xs">
                    <summary class="cursor-pointer text-gray-600 hover:text-gray-900">
                      View Parameters
                    </summary>
                    <pre class="mt-2 p-2 bg-white border border-gray-200 rounded text-xs overflow-x-auto">{{ formatParameters(event.parameters) }}</pre>
                  </details>
                </div>
              </div>
            </div>
          </div>

          <!-- Sync Audit Tab -->
          <div *ngIf="activeTab === 'sync'">
            <div class="space-y-4">
              <!-- Summary counters -->
              <div class="grid grid-cols-4 gap-2 text-center">
                <div class="p-2 bg-gray-100 rounded-md">
                  <div class="text-lg font-semibold text-gray-900">{{ syncAuditCounts.attempts }}</div>
                  <div class="text-xs text-gray-600">Attempts</div>
                </div>
                <div class="p-2 bg-green-50 rounded-md">
                  <div class="text-lg font-semibold text-green-800">{{ syncAuditCounts.successes }}</div>
                  <div class="text-xs text-green-700">Success</div>
                </div>
                <div class="p-2 bg-red-50 rounded-md">
                  <div class="text-lg font-semibold text-red-800">{{ syncAuditCounts.failures }}</div>
                  <div class="text-xs text-red-700">Failures</div>
                </div>
                <div class="p-2 bg-yellow-50 rounded-md">
                  <div class="text-lg font-semibold text-yellow-800">{{ syncAuditCounts.skipped }}</div>
                  <div class="text-xs text-yellow-700">Skipped</div>
                </div>
              </div>

              <button
                type="button"
                class="w-full bg-red-600 text-white px-3 py-2 text-sm rounded-md hover:bg-red-700"
                (click)="clearSyncAudit()"
              >
                Clear Log
              </button>

              <!-- Audit entries (newest first, live-updating) -->
              <div class="space-y-2">
                <h3 class="text-sm font-semibold text-gray-900">
                  Firestore Sync Log ({{ syncAuditEntries.length }})
                </h3>

                <div *ngIf="syncAuditEntries.length === 0" class="text-center py-8 text-gray-500 text-sm">
                  No sync activity recorded yet
                </div>

                <div *ngFor="let entry of syncAuditEntries" class="border border-gray-200 rounded-md p-2 bg-gray-50">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-2">
                      <span class="font-mono text-xs font-semibold uppercase text-gray-700">{{ entry.operation }}</span>
                      <span
                        class="px-1.5 py-0.5 text-xs rounded-full font-medium"
                        [ngClass]="syncStatusClass(entry.status)"
                      >{{ entry.status }}</span>
                      <span *ngIf="entry.durationMs !== undefined" class="text-xs text-gray-400">{{ entry.durationMs }}ms</span>
                    </div>
                    <div class="text-xs text-gray-500">{{ formatTimestamp(entry.timestamp) }}</div>
                  </div>
                  <div *ngIf="formatSyncFields(entry)" class="mt-1 font-mono text-xs text-gray-600 break-all">
                    {{ formatSyncFields(entry) }}
                  </div>
                  <div *ngIf="entry.detail" class="mt-1 text-xs break-all"
                    [ngClass]="entry.status === 'failure' ? 'text-red-700' : 'text-gray-500'">
                    {{ entry.detail }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Environment Tab -->
          <div *ngIf="activeTab === 'environment'">
            <div class="space-y-4">
              <button
                type="button"
                class="w-full bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-colors"
                (click)="refreshEnvironmentData()"
              >
                Refresh
              </button>

              <div class="space-y-2">
                <h3 class="text-sm font-semibold text-gray-900 mb-3">
                  Environment Configuration
                </h3>
                <div *ngFor="let item of environmentInfo" class="flex justify-between items-start p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <span class="text-sm font-medium text-gray-700">{{ item.key }}</span>
                  <span class="text-sm text-gray-900 font-mono text-right break-all ml-4">{{ item.value }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Local Storage Modal (embedded) -->
<app-debug-local-storage
  [isVisible]="showLocalStorageModal"
  (closed)="closeLocalStorageModal()"
></app-debug-local-storage>
